import React, { useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import styles from 'components/Order/OrderDetails/AttachemntsData.module.css'
import {
  addAttachmentToOrder,
  downloadAttachment,
  getOrderAttachment,
  removeAttachmentFromOrder,
  setAttachmentContractorAccessAs,
  setAttachmentDriverAccessAs,
  setAttachmentVerifiedAs
} from 'services'
import { AttachmentsPreviewDialog } from 'components/Order/OrderDetails/AttachmentsPreviewDialog'
import { HTTP_404_ERROR, HTTP_500_ERROR } from 'Constants'
import { useErrorHandling } from 'context/errorHandling'
import { attachmentTypes } from 'components/Order/constants'
import { useTranslation } from 'react-i18next'
import { useAuth } from 'context/auth'
import { AttachmentsDropzone } from '../OrderForms/OrderForm/AttachmentForm/AttachmentsDropzone'

export function AttachmentsData ({ order, attachmentsPreview }) {
  const id = order.id

  const [previewedAttachment, setPreviewedAttachment] = useState(null)

  const history = useHistory()
  const { t } = useTranslation()
  const { handleError } = useErrorHandling()
  const { hasAnyPrivileges } = useAuth()

  useEffect(() => {
    if (attachmentsPreview.id) {
      const previewedAttachment = order.attachments.find(attachment => attachment.id === attachmentsPreview.id)
      setPreviewedAttachment(previewedAttachment)
    } else {
      setPreviewedAttachment(null)
    }
  }, [attachmentsPreview, order])

  const onAttachmentDownload = (file) => {
    downloadAttachment({ id: file.id, filename: file.filename })
      .catch(error => handleError(error, {
        [HTTP_404_ERROR]: 'notifications.error.fileNotFound',
        [HTTP_500_ERROR]: 'notifications.error.fileDownloadError'
      }))
  }

  const setPreviewAttachment = (attachment) => history.push('/orders/details/' + id + '/attachments/' + attachment.id)

  const handleAttachmentsUpdate = (updatedFiles, type) => {
    order.attachments = order.attachments
      .filter(file => file.type !== type)
      .concat(updatedFiles.map(f => ({ ...f, type })))
  }

  const handleAttachmentRemoved = (removedFile) => {
    removeAttachmentFromOrder(id, removedFile.id).catch(handleError)
  }

  const handleAttachmentVerifiedChange = (fileId, newValue) => {
    setAttachmentVerifiedAs(fileId, newValue).catch(handleError)
  }

  const handleAttachmentDriverAccessChange = (fileId, newValue) => {
    setAttachmentDriverAccessAs(fileId, newValue).catch(handleError)
  }

  const handleAttachmentContractorAccessChange = (fileId, newValue) => {
    setAttachmentContractorAccessAs(fileId, newValue).catch(handleError)
  }

  return (
    <div className={styles.attachments}>
      {order.attachments ?
        <>
          <AttachmentsPreviewDialog open={!!previewedAttachment}
                                    initAttachment={previewedAttachment}
                                    attachmentSource={getOrderAttachment}
                                    attachments={order.attachments}
                                    onClose={() => {
                                      history.push('/orders/details/' + id)
                                    }}
                                    onAttachmentUpdated={attachmentsPreview.updateAttachment}
                                    onAttachmentRemove={attachmentsPreview.removeAttachment}
                                    onAttachmentChanged={setPreviewAttachment}
          />
          {attachmentTypes
            .filter(type => hasAnyPrivileges([type.requiredPrivilege.read]))
            .map(type =>
              <AttachmentsDropzone key={type.value}
                                   initialAttachments={order.attachments.filter(att => att.type === type.value)}
                                   onAttachmentsUpdated={(updated) => handleAttachmentsUpdate(updated, type.value)}
                                   label={`form.order.attachments.${type.label}`}
                                   type={type.value}
                                   disabled={!hasAnyPrivileges([type.requiredPrivilege.write])}
                                   chipInputProps={{
                                     onDownload: onAttachmentDownload,
                                     onShow: setPreviewAttachment
                                   }}
                                   uploadAttachment={file => addAttachmentToOrder(id, file, type.value)}
                                   onAttachmentRemoved={handleAttachmentRemoved}
                                   onAttachmentVerifiedChange={handleAttachmentVerifiedChange}
                                   onAttachmentDriverAccessChange={handleAttachmentDriverAccessChange}
                                   onAttachmentContractorAccessChange={handleAttachmentContractorAccessChange}
                                   shouldUpdateOnChange
              />)}
        </>
        : <div className={'pb-3'}>{t(`form.order.attachments.noData`)}</div>}
    </div>
  )
}

